/* ============================================================
   NEW VISION — interaction layer (vanilla, dependency-free)
   - Scroll reveal (IntersectionObserver)
   - Kinetic word swap
   - Header: scrolled state + mobile drawer + a11y dropdowns
   - Stat count-up
   - Multi-step lead qualifier -> lazy-mounts HubSpot form
   Idempotent; re-inits on Shopify section load in the editor.
   ============================================================ */
(function () {
  'use strict';
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.nv-reveal:not([data-nv-revealed])');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('nv-in'); el.setAttribute('data-nv-revealed', '1'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('nv-in'); io.unobserve(e.target); }
      });
      /* threshold MUST stay 0: a percentage threshold can never be met by an
         element taller than the viewport (the policy pages are one ~20,000px
         block), which left them permanently at opacity:0. Fixed 2026-07-29. */
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { el.setAttribute('data-nv-revealed', '1'); io.observe(el); });
  }

  /* ---------- Kinetic word swap ---------- */
  function initKinetic(root) {
    var els = (root || document).querySelectorAll('[data-nv-kinetic]:not([data-nv-kin-on])');
    els.forEach(function (el) {
      el.setAttribute('data-nv-kin-on', '1');
      var words;
      try { words = JSON.parse(el.getAttribute('data-nv-kinetic')); } catch (err) { words = []; }
      if (!words || words.length < 2) return;
      if (REDUCED) { el.textContent = words[0]; return; }
      var i = 0;
      el.textContent = words[0];
      setInterval(function () {
        i = (i + 1) % words.length;
        el.classList.add('nv-swap');
        setTimeout(function () { el.textContent = words[i]; el.classList.remove('nv-swap'); }, 200);
      }, 2000);
    });
  }

  /* ---------- Stat count-up ---------- */
  function initCountup(root) {
    var els = (root || document).querySelectorAll('[data-nv-countup]:not([data-nv-count-on])');
    if (!els.length) return;
    els.forEach(function (el) { el.setAttribute('data-nv-count-on', '1'); });
    if (REDUCED || !('IntersectionObserver' in window)) { return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        var el = e.target;
        var target = parseFloat(el.getAttribute('data-nv-countup')) || 0;
        var suffix = el.getAttribute('data-nv-suffix') || '';
        var prefix = el.getAttribute('data-nv-prefix') || '';
        var dur = 1200, start = null;
        function step(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          var val = Math.round(target * eased);
          el.textContent = prefix + val.toLocaleString() + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    var header = document.querySelector('[data-nv-header]');
    if (!header || header.getAttribute('data-nv-header-on')) return;
    header.setAttribute('data-nv-header-on', '1');

    var onScroll = function () {
      if (window.scrollY > 16) header.classList.add('nv-h-scrolled');
      else header.classList.remove('nv-h-scrolled');
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    var toggle = header.querySelector('[data-nv-menu-toggle]');
    var drawer = header.querySelector('[data-nv-drawer]');
    function setDrawer(open) {
      if (!drawer) return;
      header.classList.toggle('nv-drawer-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
    }
    if (toggle && drawer) {
      toggle.addEventListener('click', function () { setDrawer(!header.classList.contains('nv-drawer-open')); });
      drawer.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', function () { setDrawer(false); }); });
      var closeBtn = header.querySelector('[data-nv-drawer-close]');
      if (closeBtn) closeBtn.addEventListener('click', function () { setDrawer(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setDrawer(false); });
    }

    /* mobile accordions inside drawer */
    header.querySelectorAll('[data-nv-acc-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = btn.nextElementSibling;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.style.maxHeight = open ? '0px' : (panel.scrollHeight + 'px');
      });
    });

    /* desktop dropdown a11y (hover handled by CSS; this adds keyboard) */
    header.querySelectorAll('[data-nv-dd]').forEach(function (dd) {
      var trigger = dd.querySelector('[data-nv-dd-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var open = dd.classList.toggle('nv-dd-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      document.addEventListener('click', function (e) {
        if (!dd.contains(e.target)) { dd.classList.remove('nv-dd-open'); trigger.setAttribute('aria-expanded', 'false'); }
      });
    });
  }

  /* ---------- Multi-step lead qualifier ---------- */
  function nvGetCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  function initQualifier(root) {
    var qzs = (root || document).querySelectorAll('[data-nv-qualifier]:not([data-nv-qz-on])');
    qzs.forEach(function (qz) {
      qz.setAttribute('data-nv-qz-on', '1');
      var steps = Array.prototype.slice.call(qz.querySelectorAll('[data-nv-step]'));
      if (!steps.length) return;
      var bar = qz.querySelector('[data-nv-bar]');
      var backBtn = qz.querySelector('[data-nv-back]');
      var counter = qz.querySelector('[data-nv-counter]');
      var answers = {};
      var idx = 0;

      function render(initial) {
        steps.forEach(function (s, i) { s.hidden = (i !== idx); });
        var pct = Math.round(((idx) / (steps.length - 1)) * 100);
        if (bar) bar.style.width = pct + '%';
        if (counter) counter.textContent = 'Step ' + (idx + 1) + ' of ' + steps.length;
        if (backBtn) backBtn.style.visibility = idx === 0 ? 'hidden' : 'visible';
        var active = steps[idx];
        if (active.getAttribute('data-step-type') === 'form') mountForm(active);
        if (!initial) {
          var h = active.querySelector('.nv-qz-q');
          if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) {} }
          var rect = qz.getBoundingClientRect();
          if (rect.top < 70 || rect.bottom > window.innerHeight) {
            try { qz.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' }); } catch (e) {}
          }
        }
      }

      function next() { if (idx < steps.length - 1) { idx++; render(false); } }
      function back() { if (idx > 0) { idx--; render(false); } }

      steps.forEach(function (step) {
        var key = step.getAttribute('data-step-key') || ('q' + Math.random());
        step.querySelectorAll('.nv-qz-opt').forEach(function (opt) {
          opt.addEventListener('click', function () {
            step.querySelectorAll('.nv-qz-opt').forEach(function (o) { o.classList.remove('nv-sel'); o.setAttribute('aria-pressed', 'false'); });
            opt.classList.add('nv-sel'); opt.setAttribute('aria-pressed', 'true');
            answers[key] = { value: opt.getAttribute('data-value') || opt.textContent.trim(), label: opt.getAttribute('data-label') || opt.textContent.trim() };
            setTimeout(next, 260);
          });
        });
        var cont = step.querySelector('[data-nv-continue]');
        if (cont) cont.addEventListener('click', next);
      });
      if (backBtn) backBtn.addEventListener('click', back);

      /* On the form step, just fill the human-readable summary chip. */
      function mountForm(step) {
        var sum = step.querySelector('[data-nv-summary]');
        if (!sum) return;
        var parts = [];
        ['goal', 'org', 'size'].forEach(function (k) { if (answers[k] && answers[k].label) parts.push(answers[k].label); });
        sum.textContent = parts.length ? parts.join('  ·  ') : '';
        sum.hidden = parts.length === 0;
      }

      /* Custom contact form -> HubSpot Forms Submission API.
         Folds the qualifier answers into notes_questions so the rep
         sees making/org/size on the lead; then redirects to the
         thank-you page (which fires generate_lead). */
      var leadForm = qz.querySelector('[data-nv-qz-form]');
      if (leadForm && !leadForm.getAttribute('data-nv-form-on')) {
        leadForm.setAttribute('data-nv-form-on', '1');
        leadForm.addEventListener('submit', function (e) {
          e.preventDefault();
          if (!leadForm.checkValidity()) { leadForm.reportValidity(); return; }
          var btn = leadForm.querySelector('[data-nv-qz-submit]');
          var errEl = leadForm.querySelector('[data-nv-qz-err]');
          if (errEl) errEl.hidden = true;
          var btnHtml = btn ? btn.innerHTML : '';
          if (btn) { btn.disabled = true; btn.innerHTML = 'Sending…'; }

          var data = new FormData(leadForm);
          var noteLines = [];
          var labels = { goal: 'Making', org: 'For', size: 'Group size' };
          ['goal', 'org', 'size'].forEach(function (k) { if (answers[k]) noteLines.push(labels[k] + ': ' + answers[k].label); });
          var note = 'Lead qualifier — ' + noteLines.join(' · ');
          var userNote = (data.get('notes') || '').toString().trim();
          if (userNote) note += '\n\nThey added: ' + userNote;

          var fields = [
            { name: 'firstname', value: (data.get('firstname') || '').toString().trim() },
            { name: 'lastname', value: (data.get('lastname') || '').toString().trim() },
            { name: 'email', value: (data.get('email') || '').toString().trim() },
            { name: 'company', value: (data.get('company') || '').toString().trim() },
            { name: 'notes_questions', value: note }
          ];
          var portal = qz.getAttribute('data-hs-portal');
          var formId = qz.getAttribute('data-hs-form');
          var redirect = qz.getAttribute('data-redirect') || '/pages/form-submitted';

          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'lead_qualifier_submit', pyc_goal: (answers.goal || {}).value, pyc_org: (answers.org || {}).value, pyc_size: (answers.size || {}).value });
          } catch (e) {}

          var payload = { fields: fields, context: { pageUri: window.location.href, pageName: document.title } };
          var hutk = nvGetCookie('hubspotutk');
          if (hutk) payload.context.hutk = hutk;

          var done = false;
          function fail(msg) {
            if (done) return; done = true;
            if (btn) { btn.disabled = false; btn.innerHTML = btnHtml; }
            if (errEl) { errEl.hidden = false; errEl.textContent = msg || 'Something went wrong. Please try again, or email hello@printyourcause.com.'; }
          }
          if (!portal || !formId) { fail(); return; }

          fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + portal + '/' + formId, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
          }).then(function (r) {
            if (r.ok) { done = true; window.location.href = redirect; return; }
            return r.json().then(function (j) {
              var m = j && (j.message || (j.errors && j.errors[0] && j.errors[0].message));
              fail(m && /email/i.test(m) ? 'Please enter a valid email address.' : null);
            }).catch(function () { fail(); });
          }).catch(function () { fail("Couldn't reach the server — please check your connection and try again."); });
        });
      }

      render(true);
    });
  }

  /* ---------- Boot ---------- */
  function boot(root) {
    initReveal(root);
    initKinetic(root);
    initCountup(root);
    initQualifier(root);
    initHeader();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);

  /* Shopify theme editor re-init */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:reorder', function () { boot(document); });
})();
